import { Environment, Subsidiary } from '@coolblue-development/constants';
import { generateUrl } from '@coolblue-development/next-routing/server';

type EnvironmentUrls = Record<Environment, string>;
type ShopOutletIds = Record<Subsidiary, number>;

const prixcillaUrls: EnvironmentUrls = {
  development: 'https://duprix-web.s3-vpc-endpoint-proxy.eu-west-1.coolblue-testing.eu',
  ci: 'https://duprix-web.s3-vpc-endpoint-proxy.eu-west-1.coolblue-testing.eu',
  testing: 'https://duprix-web.s3-vpc-endpoint-proxy.eu-west-1.coolblue-testing.eu',
  acceptance: 'https://duprix-web.s3-vpc-endpoint-proxy.eu-west-1.coolblue-acceptance.eu',
  production: 'https://prixcilla.office.coolblue.eu'
};

const flirtUrls: EnvironmentUrls = {
  development: 'https://flirt.tst.zone',
  ci: 'https://flirt.tst.zone',
  testing: 'https://flirt.tst.zone',
  acceptance: 'https://flirt.accept.zone',
  production: 'https://flirt.office.coolblue.nl'
};

const shopOutletIds: ShopOutletIds = {
  nl: 0,
  be: 361,
  de: 12795
};

const getEnvironmentBaseUrl = (urls: EnvironmentUrls): string => {
  const environment =
    (process.env.NEXT_PUBLIC_ENVIRONMENT as Environment) || Environment.PRODUCTION;
  return urls[environment];
};

const vanessaBaseUrl = `${getEnvironmentBaseUrl(flirtUrls)}/Flirt/VanessaLauncher/popup.vanessalaunchmanagemodel.details.html`;

const getShopOutletId = (subsidiary: Subsidiary): number => {
  return shopOutletIds[subsidiary];
};

export const getPrixcillaUrl = (productId: number): string | undefined => {
  if (!productId || isNaN(productId)) {
    return undefined;
  }
  return `${getEnvironmentBaseUrl(prixcillaUrls)}/strategy-pricing/product-overview/${productId}`;
};

export const getPriceTagGeneratorUrl = async (
  productId: number,
  slug: string
): Promise<string | undefined> => {
  return await generateUrl(
    'product.card.coolblue',
    {
      productId: productId.toString(),
      slug: slug ?? ''
    },
    { absoluteUrl: true }
  );
};

export const getVanessaUrl = (productId: number): URL | undefined => {
  if (!productId || isNaN(productId)) {
    return undefined;
  }

  const vanessaUrl = new URL(vanessaBaseUrl);

  vanessaUrl.search = `product=${productId}`;
  return vanessaUrl;
};

export const getVanessaClipboardUrl = (
  productId: number,
  subsidiary: Subsidiary
): URL | undefined => {
  const shopOutletId = getShopOutletId(subsidiary);
  if (!productId || isNaN(productId) || shopOutletId === undefined || isNaN(shopOutletId)) {
    return undefined;
  }

  const vanessaUrl = new URL(vanessaBaseUrl);

  vanessaUrl.search = `clipboardproduct=${productId}&shopoutletid=${shopOutletId}`;
  return vanessaUrl;
};

export const getVanessaInsurancesUrl = (
  productId: number,
  subsidiary: Subsidiary
): URL | undefined => {
  const shopOutletId = getShopOutletId(subsidiary);
  if (!productId || isNaN(productId) || shopOutletId === undefined || isNaN(shopOutletId)) {
    return undefined;
  }

  const vanessaUrl = new URL(vanessaBaseUrl);

  vanessaUrl.search = `insuranceforproduct=${productId}&shopoutletid=${shopOutletId}`;
  return vanessaUrl;
};

export const getIrisUrl = (productId: number, subsidiary: Subsidiary): URL | undefined => {
  const shopOutletId = getShopOutletId(subsidiary);
  if (!productId || isNaN(productId) || shopOutletId === undefined || isNaN(shopOutletId)) {
    return undefined;
  }

  const irisUrl = new URL(`${getEnvironmentBaseUrl(flirtUrls)}/productdashboard.php`);
  irisUrl.search = `productid=${productId}&shop_outlet_id=${shopOutletId}`;
  return irisUrl;
};

export const getStockUrl = (productId: number): URL | undefined => {
  if (!productId || isNaN(productId)) {
    return undefined;
  }

  const stockUrl = new URL(`${getEnvironmentBaseUrl(flirtUrls)}/productdashboard.php`);
  stockUrl.search = `productid=${productId}`;
  return stockUrl;
};
